import React from 'react';
import { Info } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip';
import { cn } from '../../../lib/utils';

// Tag colours by role in the wagering decision.
export const TAG_ROLE_CLASSES = {
  applies: 'border border-positive/40 bg-positive/15 text-positive',
  conflict: 'border border-warning/40 bg-warning/15 text-warning',
  overridden: 'border border-white/10 bg-surface-2 text-ink-mute line-through',
  cc_exception: 'border border-brand-purple/60 bg-brand-purple/30 text-ink',
  other: 'border border-white/10 text-ink-dim',
};

const HEADLINE_CLASSES = {
  positive: 'border-positive/40 bg-positive/15 text-positive',
  warning: 'border-warning/40 bg-warning/15 text-warning',
  muted: 'border-white/10 bg-surface-2 text-ink-dim',
};

const STATE_LABELS = { applies: 'applies', conflict: 'in conflict', overridden: 'overridden' };

// PlayerTag: one tag, coloured by its role (see TAG_ROLE_CLASSES).
export function PlayerTag({ tag, role }) {
  return <Badge variant="outline" className={cn('font-bold', TAG_ROLE_CLASSES[role])}>{tag}</Badge>;
}

function RuleLine({ row }) {
  return (
    <li className={cn('space-y-0.5', row.state === 'overridden' && 'opacity-70')}>
      <div>
        <span className="font-bold">{row.tag}</span>
        <span className="text-ink-dim"> → {row.wagering}x · {row.rule} · tier {row.tier} · {STATE_LABELS[row.state]}</span>
      </div>
      {row.description && <div className="text-ink-mute">{row.description}</div>}
    </li>
  );
}

// WageringRequirement: the comp's wagering as a pill; hovering explains it — which tag decides,
// what it overrides, and every matched rule.
export default function WageringRequirement({ view, label = true }) {
  return (
    <div>
      {label && <div className="mb-1 text-xs font-medium text-ink-mute">Wagering requirement</div>}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Wagering requirement ${view.headline}: ${view.reason}`}
            className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-black', HEADLINE_CLASSES[view.tone])}
          >
            {view.headline}
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-sm space-y-2 p-3">
          {view.reason && <p className="font-bold">{view.reason}</p>}
          {view.rows.length > 0 && <ul className="space-y-1.5">{view.rows.map((r) => <RuleLine key={r.tag} row={r} />)}</ul>}
          <p className="text-ink-mute">Rules from bs_persist.vip_comp_wagering_tags · the lowest tier wins</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
